<template>
    <div class="product-v3-container">
        <!-- Stats Header Cards -->
        <ResourceStatsNavigation
            :stats="stats"
            :activeTab="activeTab"
            :can-consult-salle="canConsultSalle"
            :can-consult-material="canConsultMaterial"
            @click="setActiveTab"
        />

        <!-- Main Content Card -->
        <div class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
            <!-- Tab Title -->
            <div class="ppv2-flex ppv2-items-center ppv2-justify-between">
                <Header :title="tabTitle" />
            </div>

            <!-- Loading State -->
            <div v-if="isLoading" class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-py-20">
                <div
                    class="ppv2-animate-spin ppv2-rounded-full ppv2-h-10 ppv2-w-10 ppv2-border-4 ppv2-border-purple-600 ppv2-border-t-transparent"
                ></div>
            </div>

            <!-- Tab Content -->
            <component
                v-else
                :is="currentTabComponent"
                :key="activeTab"
                :user-id="userId"
                :initial-type="currentTabInitialType"
                source="resource"
                @refresh-stats="fetchStats"
                :token="token"
            />
        </div>
    </div>
</template>

<script>
import EquipementsTable from '../Equipements/index.vue'
import ResourceStatsNavigation from './ResourceStatsNavigation.vue'
import normalizeSize from '../../../utils/normalizeSize'
import axios from 'axios'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import Header from '../../Shared/Header.vue'

export default {
    name: 'ResourceV3Index',
    components: {
        ResourceStatsNavigation,
        Header,
        EquipementsTable,
    },
    props: {
        userId: {
            type: [Number, String, null],
            default: null,
        },
        initialTab: {
            type: String,
            default: 'installations',
        },
        canConsultSalle: {
            type: Boolean,
            default: true,
        },
        canConsultMaterial: {
            type: Boolean,
            default: true,
        },
        token: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            activeTab: this.initialTab,
            isLoading: true,
            stats: {
                installations: 0,
                equipements: 0,
            },
        }
    },
    computed: {
        currentTabComponent() {
            return 'EquipementsTable'
        },
        tabTitle() {
            const titles = {
                installations: this.$t('experiences.navigation.installations') || 'Installations',
                equipements: this.$t('experiences.navigation.equipements') || 'Équipements',
            }
            return titles[this.activeTab] || this.$t('experiences.navigation.installations') || 'Installations'
        },
        currentTabInitialType() {
            const types = {
                installations: 'salle',
                equipements: 'materiel',
            }
            return types[this.activeTab] || 'salle'
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        setActiveTab(tab) {
            this.activeTab = tab
            // Update URL query parameter
            const url = new URL(window.location.href)
            url.searchParams.set('tab', tab)
            window.history.pushState({}, '', url.toString())
        },
        async fetchStats() {
            try {
                const response = await axios.get('/api/v3/experience/stats', {
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                this.stats = response.data.data
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.isLoading = false
            }
        },
    },
    mounted() {
        this.fetchStats()

        // Initialize active tab from URL or prop, respecting permissions
        const urlParams = new URLSearchParams(window.location.search)
        const tabParam = urlParams.get('tab')

        if (tabParam && ['installations', 'equipements'].includes(tabParam)) {
            // Check if user has permission for the tab in URL
            if (tabParam === 'installations' && !this.canConsultSalle) {
                this.setActiveTab('equipements')
            } else if (tabParam === 'equipements' && !this.canConsultMaterial) {
                this.setActiveTab('installations')
            } else {
                this.activeTab = tabParam
            }
        } else if (this.initialTab) {
            // Check if user has permission for initialTab
            if (this.initialTab === 'installations' && !this.canConsultSalle) {
                this.activeTab = 'equipements'
            } else if (this.initialTab === 'equipements' && !this.canConsultMaterial) {
                this.activeTab = 'installations'
            } else {
                this.activeTab = this.initialTab
            }
        }

        // Final fallback if activeTab is still invalid due to permissions
        if (this.activeTab === 'installations' && !this.canConsultSalle) {
            this.activeTab = 'equipements'
        } else if (this.activeTab === 'equipements' && !this.canConsultMaterial) {
            this.activeTab = 'installations'
        }

        // Remove white background from parent container for V3 layout
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.add('product-v3-no-bg')
        }
    },
    beforeDestroy() {
        // Restore background when leaving the page
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.remove('product-v3-no-bg')
        }
    },
}
</script>

<style scoped lang="scss">
.product-v3-container {
    padding: 0;
}
</style>

<style lang="scss">
#main-container.product-v3-no-bg {
    background: transparent !important;
    border-radius: 0px;
}

#main-container:has(.product-v3-container) {
    background: transparent !important;
}

.dark #main-container.product-v3-no-bg,
.dark #main-container:has(.product-v3-container) {
    background: transparent !important;
}

#page-header {
    display: none;
}
</style>
